import {sharedCampaigns} from '@cl/shared';
import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {DTScheduleNode, ScheduleType} from 'common/src/modules/decisionTree/decisionTree.types';
import {NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import {TimezoneSource} from 'common/src/modules/timezones/timezones.types';
import {UTCAbbreviation} from 'common/src/modules/timezones/timezones.consts';
import {CLStore} from 'common/src/redux/store';

function createDefaultScheduleCard(): DTScheduleNode {
  return {
    id: NO_NODE_ID,
    name: 'Schedule Card',
    description: '',
    type: NodeType.SCHEDULE,
    outputs: [
      {
        id: 1,
        name: 'Fallback',
        type: OutputType.Fallback,
        nodeId: NO_NODE_ID,
      },
    ],
    inputs: [],
    scheduleType: ScheduleType.DateRange,
    timezoneSource: TimezoneSource.Advertiser,
    timeZone: UTCAbbreviation,
    randomizeOutputs: false,
  };
}

function ensureFallbackOutputExists(card: DTScheduleNode): void {
  const hasFallback = card.outputs.some((output) => output.type === OutputType.Fallback);

  if (!hasFallback) {
    const nextId = card.outputs.length > 0 ? Math.max(...card.outputs.map((o) => o.id), 0) + 1 : 1;

    card.outputs.push({
      id: nextId,
      name: 'Fallback',
      type: OutputType.Fallback,
      nodeId: NO_NODE_ID,
    });
  }
}

/**
 * Gets an existing schedule card by ID, or creates a new default one.
 * @param cardId - The card ID to fetch, or 0/undefined for a new card
 * @returns The schedule card (existing or new), fully mutable
 */
async function getOrCreateScheduleCard(
  cardId: number | undefined,
  _store: CLStore,
): Promise<DTScheduleNode> {
  const resolvedCardId = cardId && cardId > NO_NODE_ID ? cardId : NO_NODE_ID;

  if (resolvedCardId) {
    const existingCard = sharedCampaigns.nodesRef.value[resolvedCardId];

    if (!existingCard) {
      throw new Error(`Card with id ${resolvedCardId} not found`);
    }

    if (existingCard.type !== NodeType.SCHEDULE) {
      throw new Error(`Card with id ${resolvedCardId} is not a schedule card`);
    }

    ensureFallbackOutputExists(existingCard as DTScheduleNode);

    return existingCard as DTScheduleNode;
  }

  return createDefaultScheduleCard();
}

export default getOrCreateScheduleCard;
export {createDefaultScheduleCard};
